import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import FilterPanel from './components/FilterPanel';
import ProfileCard from './components/ProfileCard';
import ProfileDetailModal from './components/ProfileDetailModal';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';
import MatchNotificationModal from '../../components/ui/MatchNotificationModal';

import { useAuth } from '../../contexts/AuthContext';
import { profileService, likesService, profileViewsService } from '../../services/supabaseService';

const SearchDiscovery = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  // Use gender from userProfile (set at profile creation) so real users show correct discovery
  const userGender = userProfile?.gender || user?.user_metadata?.gender || 'male';
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [likedProfiles, setLikedProfiles] = useState(new Set());

  const [filters, setFilters] = useState({
    locationRadius: 25,
    ageMin: 25,
    ageMax: 35,
    relationshipStatus: 'all',
    sortBy: 'relevance',
    verification: 'all',
    activityLevel: 'all',
    onlineOnly: false,
    withPhotos: false, // Show all profiles by default; users can filter "With photos" if they want
    interests: []
  });

  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [currentMatch, setCurrentMatch] = useState(null);

  // Load profiles when user and gender are available (so real users get correct opposite-gender list)
  useEffect(() => {
    if (!user?.id) return;
    loadProfiles();
    checkSubscription();
    loadMyLikes();
  }, [user?.id, userGender]);

  useEffect(() => {
    applyFilters();
  }, [filters, profiles]);

  const checkSubscription = async () => {
    if (!user) return;
    try {
      const profile = await profileService?.getProfile(user?.id);
      // Check for active subscription (premium, active, or trialing)
      const isActive = 
        profile?.subscriptionStatus === 'active' || 
        profile?.subscriptionStatus === 'premium' ||
        profile?.subscriptionStatus === 'trialing';
      setHasActiveSubscription(isActive);
    } catch (error) {
      console.error('Subscription check error:', error);
    }
  };

  const loadProfiles = async () => {
    setIsLoading(true);
    try {
      // Get opposite gender profiles
      const targetGender = userGender === 'male' ? 'female' : 'male';
      const data = await profileService?.searchProfiles({
        gender: targetGender,
        ...filters
      });

      // Filter out current user
      const filteredData = data?.filter(p => p?.id !== user?.id) || [];
      setProfiles(filteredData);
      setFilteredProfiles(filteredData);
    } catch (error) {
      console.error('Load profiles error:', error);
      setProfiles([]);
      setFilteredProfiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMyLikes = async () => {
    if (!user) return;
    try {
      const likes = await likesService?.getMyLikes(user?.id);
      const likedIds = new Set(likes?.map(like => like?.likedId));
      setLikedProfiles(likedIds);
    } catch (error) {
      console.error('Load likes error:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...profiles];

    if (filters?.onlineOnly) {
      filtered = filtered?.filter(p => p?.isOnline);
    }

    if (filters?.verification === 'verified') {
      filtered = filtered?.filter(p => p?.isVerified);
    }

    if (filters?.withPhotos) {
      filtered = filtered?.filter(p => p?.photos?.length > 0);
    }

    // Sort by online status first (TRUE first), then maintain existing order
    filtered.sort((a, b) => {
      if (a.isOnline === b.isOnline) return 0;
      return a.isOnline ? -1 : 1;
    });

    setFilteredProfiles(filtered);
  };

  const handleResetFilters = () => {
    setFilters({
      locationRadius: 25,
      ageMin: 25,
      ageMax: 35,
      relationshipStatus: 'all',
      sortBy: 'relevance',
      verification: 'all',
      activityLevel: 'all',
      onlineOnly: false,
      withPhotos: false,
      interests: []
    });
    loadProfiles();
  };

  const handleViewProfile = async (profileId) => {
    if (!user) return;
    
    const profile = profiles?.find(p => p?.id === profileId);
    if (profile) {
      setSelectedProfile(profile);
      // Record profile view
      try {
        await profileViewsService?.recordProfileView(user?.id, profileId);
      } catch (error) {
        console.error('Record view error:', error);
      }
    }
  };

  const handleExpressInterest = async (profileId) => {
    if (!user) return;

    try {
      if (likedProfiles?.has(profileId)) {
        await likesService?.removeLike(user?.id, profileId);
        setLikedProfiles(prev => {
          const newSet = new Set(prev);
          newSet?.delete(profileId);
          return newSet;
        });
      } else {
        const result = await likesService?.createLike(user?.id, profileId);
        setLikedProfiles(prev => new Set([...prev, profileId]));
        
        // Check if it's a mutual match
        if (result?.isMutual) {
          // Show match notification modal
          const matchedProfile = profiles?.find(p => p?.id === profileId);
          if (matchedProfile) {
            setCurrentMatch({
              id: matchedProfile?.id,
              displayName: matchedProfile?.displayName,
              image: matchedProfile?.primaryPhoto,
              imageAlt: matchedProfile?.primaryPhotoAlt,
              matchedUserImage: userProfile?.primaryPhoto,
              matchedUserImageAlt: userProfile?.primaryPhotoAlt
            });
          }
        }
      }
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  const handleSendMessage = async (profileId) => {
    if (userGender === 'female' || hasActiveSubscription) {
      // Navigate to messaging system and start conversation
      navigate('/messaging-system', { state: { startConversationWith: profileId } });
    } else {
      // Redirect to subscription page for males without premium
      navigate('/subscription-payment');
    }
  };

  return (
    <>
      <Helmet>
        <title>Discover Profiles - Bamchik Discreet Dating</title>
        <meta name="description" content="Browse anonymous profiles with advanced privacy controls on Bamchik discreet dating platform" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1400px] px-4 lg:px-6 py-6 md:py-8 lg:py-10">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="lg:w-80 flex-shrink-0">
              <FilterPanel
                filters={filters}
                onFilterChange={setFilters}
                onApplyFilters={loadProfiles}
                onResetFilters={handleResetFilters}
              />
            </div>

            <div className="flex-1">
              {isLoading ? (
                <LoadingState />
              ) : filteredProfiles?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  {filteredProfiles?.map((profile, index) => (
                    <ProfileCard
                      key={profile?.id}
                      profile={{
                        ...profile,
                        image: profile?.primaryPhoto,
                        imageAlt: profile?.primaryPhotoAlt,
                        displayName: profile?.displayName,
                        location: profile?.city,
                        compatibility: Math.floor(Math.random() * 30) + 70,
                        lastActive: profile?.isOnline ? 'Online now' : null,
                        // Use real verification status from database
                        identityVerificationStatus: profile?.identityVerificationStatus,
                        photoVerificationStatus: profile?.photoVerificationStatus,
                        isVerified: profile?.isVerified,
                        backgroundCheck: false // Not implemented yet
                      }}
                      onViewProfile={handleViewProfile}
                      onSendMessage={handleSendMessage}
                      onExpressInterest={handleExpressInterest}
                      userGender={userGender}
                      hasActiveSubscription={hasActiveSubscription}
                      isLiked={likedProfiles?.has(profile?.id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState onResetFilters={handleResetFilters} />
              )}
            </div>
          </div>
        </main>

        {selectedProfile && (
          <ProfileDetailModal
            profile={{
              ...selectedProfile,
              image: selectedProfile?.primaryPhoto,
              imageAlt: selectedProfile?.primaryPhotoAlt,
              photos: selectedProfile?.photos,
              photosAlt: selectedProfile?.photosAlt,
              displayName: selectedProfile?.displayName,
              location: selectedProfile?.city,
              compatibility: Math.floor(Math.random() * 30) + 70,
              lastActive: selectedProfile?.isOnline ? 'Online now' : null
            }}
            onClose={() => setSelectedProfile(null)}
            onSendMessage={handleSendMessage}
            onExpressInterest={handleExpressInterest}
            userGender={userGender}
            hasActiveSubscription={hasActiveSubscription}
          />
        )}

        {currentMatch && (
          <MatchNotificationModal
            match={currentMatch}
            onClose={() => setCurrentMatch(null)}
            onSendMessage={(profileId) => {
              handleSendMessage(profileId);
              setCurrentMatch(null);
            }}
            onViewProfile={(profileId) => {
              const profile = profiles?.find(p => p?.id === profileId);
              if (profile) {
                setSelectedProfile(profile);
                setCurrentMatch(null);
              }
            }}
          />
        )}
      </div>
    </>
  );
};

export default SearchDiscovery;